import React, { useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiMoreHorizontal, FiMaximize2, FiMinimize2, FiX, FiSettings,
  FiRefreshCw, FiDownload, FiEye, FiEyeOff, FiMove, FiEdit3
} from 'react-icons/fi';
import { useTheme } from '../contexts/ThemeContext';

// Simple drag and drop implementation without external dependencies
type DragCollect = { isDragging: boolean };
type UseDragReturn = [DragCollect, (node?: HTMLElement | null) => void, (node?: HTMLElement | null) => void];
const useDrag = (spec?: any): UseDragReturn => [{ isDragging: false }, () => {}, () => {}];
type UseDropReturn = [null, (node?: HTMLElement | null) => void];
const useDrop = (spec?: any): UseDropReturn => [null, () => {}];
const DndProvider = ({ children }: { children: React.ReactNode }) => <>{children}</>;
const Resizable = ({ children, ...props }: any) => <div {...props}>{children}</div>;

export interface WidgetConfig {
  id: string;
  title: string;
  type: 'chart' | 'stat' | 'table' | 'text' | 'custom';
  position: { x: number; y: number };
  size: { width: number; height: number };
  minSize?: { width: number; height: number };
  maxSize?: { width: number; height: number };
  isResizable?: boolean;
  isDraggable?: boolean;
  isVisible?: boolean;
  refreshInterval?: number; // in seconds
  data?: any;
  settings?: Record<string, any>;
  lastUpdated?: Date;
}

interface CustomWidgetProps {
  widget: WidgetConfig;
  onUpdate?: (widget: WidgetConfig) => void;
  onDelete?: (widgetId: string) => void;
  onRefresh?: (widgetId: string) => void;
  onExport?: (widgetId: string, format: 'png' | 'pdf' | 'json') => void;
  isEditing?: boolean;
  children: React.ReactNode;
  className?: string;
}

const ITEM_TYPE = 'widget';

const CustomWidget: React.FC<CustomWidgetProps> = ({
  widget,
  onUpdate,
  onDelete,
  onRefresh,
  onExport,
  isEditing = false,
  children,
  className = ''
}) => {
  const [isMaximized, setIsMaximized] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [tempTitle, setTempTitle] = useState(widget.title);
  const { themeMode } = useTheme();
  
  const widgetRef = useRef<HTMLDivElement>(null);
  const titleInputRef = useRef<HTMLInputElement>(null);

  // Drag functionality
  const [{ isDragging }, drag, dragPreview] = useDrag({
    type: ITEM_TYPE,
    item: { id: widget.id, type: ITEM_TYPE },
    canDrag: isEditing && widget.isDraggable !== false,
    collect: (monitor: { isDragging: () => boolean }) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  // Drop functionality for reordering
  const [, drop] = useDrop({
    accept: ITEM_TYPE,
    hover: (draggedItem: { id: string }) => {
      if (draggedItem.id !== widget.id) {
        // Handle widget reordering logic here
      }
    },
  });

  // Combine refs
  const combinedRef = useCallback((node: HTMLDivElement) => {
    if (node) {
      (widgetRef as any).current = node;
      drag(node);
      drop(node);
      dragPreview(node);
    }
  }, [drag, drop, dragPreview]);

  // Update widget position
  const updatePosition = (x: number, y: number) => {
    if (onUpdate) {
      onUpdate({
        ...widget,
        position: { x, y }
      });
    }
  };

  // Update widget size
  const updateSize = (width: number, height: number) => {
    if (onUpdate) {
      onUpdate({
        ...widget,
        size: { width, height }
      });
    }
  };

  // Handle refresh
  const handleRefresh = async () => {
    if (!onRefresh) return;
    
    setIsRefreshing(true);
    try {
      await onRefresh(widget.id);
    } finally {
      setIsRefreshing(false);
    }
  };

  // Handle title edit
  const handleTitleEdit = () => {
    setEditingTitle(true);
    setTimeout(() => {
      titleInputRef.current?.focus();
      titleInputRef.current?.select();
    }, 0);
  };

  const saveTitleEdit = () => {
    if (tempTitle.trim() && tempTitle !== widget.title) {
      onUpdate?.({
        ...widget,
        title: tempTitle.trim()
      });
    } else {
      setTempTitle(widget.title);
    }
    setEditingTitle(false);
  };

  const cancelTitleEdit = () => {
    setTempTitle(widget.title);
    setEditingTitle(false);
  };

  // Toggle visibility
  const toggleVisibility = () => {
    onUpdate?.({
      ...widget,
      isVisible: !widget.isVisible
    });
  };

  // Toggle maximize
  const toggleMaximize = () => {
    setIsMaximized(!isMaximized);
  };

  // Export handlers
  const handleExport = (format: 'png' | 'pdf' | 'json') => {
    onExport?.(widget.id, format);
    setShowMenu(false);
  };

  if (widget.isVisible === false && !isEditing) {
    return null;
  }

  const widgetContent = (
    <motion.div
      ref={combinedRef}
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ 
        opacity: widget.isVisible === false ? 0.5 : 1, 
        scale: 1 
      }}
      className={`
        relative rounded-lg shadow-lg overflow-hidden flex flex-col
        ${isDragging ? 'opacity-50 rotate-2 shadow-xl' : ''}
        ${isMaximized ? 'fixed inset-4 z-50' : ''}
        ${className}
      `}
      style={{
        left: isMaximized ? 0 : widget.position.x,
        top: isMaximized ? 0 : widget.position.y,
        width: '100%',
        height: isMaximized ? '100%' : widget.size.height,
        background: 'var(--color-bg-primary)',
        border: '1px solid var(--color-border-primary)',
        ...(isEditing ? {
          outline: '2px solid var(--color-primary)',
          outlineOffset: '-2px'
        } : {})
      }}
    >
      {/* Widget Header */}
      <div
        className={`
          flex items-center justify-between p-3
          ${isEditing && widget.isDraggable !== false ? 'cursor-move' : ''}
        `}
        style={{
          background: 'var(--color-bg-primary)',
          borderBottom: '1px solid var(--color-border-primary)'
        }}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {isEditing && widget.isDraggable !== false && (
            <FiMove className="text-gray-400 flex-shrink-0" size={16} />
          )}
          
          {editingTitle ? (
            <input
              ref={titleInputRef}
              value={tempTitle}
              onChange={(e) => setTempTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') saveTitleEdit();
                if (e.key === 'Escape') cancelTitleEdit();
              }}
              className="flex-1 px-2 py-1 text-sm font-medium rounded"
              style={{
                background: 'var(--color-bg-primary)',
                color: 'var(--color-text-primary)',
                border: '1px solid var(--color-border-primary)',
                outline: 'none'
              }}
              onFocus={(e) => {
                e.target.style.border = '1px solid var(--color-primary)';
              }}
              onBlur={(e) => {
                e.target.style.border = '1px solid var(--color-border-primary)';
                saveTitleEdit();
              }}
            />
          ) : (
            <h3 
              className="font-medium truncate flex-1 cursor-pointer transition-colors"
              style={{ 
                color: 'var(--color-text-primary)',
              }}
              onMouseEnter={(e) => {
                (e.target as HTMLElement).style.color = 'var(--color-primary)';
              }}
              onMouseLeave={(e) => {
                (e.target as HTMLElement).style.color = 'var(--color-text-primary)';
              }}
              onClick={isEditing ? handleTitleEdit : undefined}
              title={widget.title}
            >
              {widget.title}
            </h3>
          )}
        </div>

        <div className="flex items-center gap-1">
          {/* Last updated indicator */}
          {widget.lastUpdated && (
            <span className="text-xs mr-2" style={{ color: 'var(--color-text-secondary)' }}>
              {new Date(widget.lastUpdated).toLocaleTimeString()}
            </span>
          )}

          {/* Refresh button */}
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="p-1 text-gray-400 hover:text-gray-600 rounded transition-colors"
            title="Refresh widget"
          >
            <FiRefreshCw 
              size={14} 
              className={isRefreshing ? 'animate-spin' : ''} 
            />
          </button>

          {/* Visibility toggle (edit mode only) */}
          {isEditing && (
            <button
              onClick={toggleVisibility}
              className="p-1 text-gray-400 hover:text-gray-600 rounded transition-colors"
              title={widget.isVisible === false ? 'Show widget' : 'Hide widget'}
            >
              {widget.isVisible === false ? (
                <FiEyeOff size={14} />
              ) : (
                <FiEye size={14} />
              )}
            </button>
          )}

          {/* Maximize/minimize button */}
          <button
            onClick={toggleMaximize}
            className="p-1 text-gray-400 hover:text-gray-600 rounded transition-colors"
            title={isMaximized ? 'Minimize' : 'Maximize'}
          >
            {isMaximized ? (
              <FiMinimize2 size={14} />
            ) : (
              <FiMaximize2 size={14} />
            )}
          </button>

          {/* Menu */}
          <div className="relative">
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="p-1 text-gray-400 hover:text-gray-600 rounded transition-colors"
              title="Widget menu"
            >
              <FiMoreHorizontal size={14} />
            </button>

            <AnimatePresence>
              {showMenu && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9, y: -10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.9, y: -10 }}
                  className="absolute right-0 top-full mt-1 w-48 rounded-lg shadow-lg z-50"
                  style={{ background: 'var(--color-bg-primary)', border: '1px solid var(--color-border-primary)' }}
                >
                  <div className="py-1">
                    <button
                      onClick={() => {/* Open settings */}}
                      className="flex items-center gap-2 w-full px-3 py-2 text-sm hover:bg-gray-100/10"
                      style={{ color: 'var(--color-text-primary)' }}
                    >
                      <FiSettings size={14} />
                      Settings
                    </button>
                    
                    <button
                      onClick={handleTitleEdit}
                      className="flex items-center gap-2 w-full px-3 py-2 text-sm hover:bg-gray-100/10"
                      style={{ color: 'var(--color-text-primary)' }}
                    >
                      <FiEdit3 size={14} />
                      Rename
                    </button>

                    <div className="my-1" style={{ borderTop: '1px solid var(--color-border-primary)' }} />
                    
                    <button
                      onClick={() => handleExport('png')}
                      className="flex items-center gap-2 w-full px-3 py-2 text-sm hover:bg-gray-100/10"
                      style={{ color: 'var(--color-text-primary)' }}
                    >
                      <FiDownload size={14} />
                      Export as PNG
                    </button>
                    
                    <button
                      onClick={() => handleExport('pdf')}
                      className="flex items-center gap-2 w-full px-3 py-2 text-sm hover:bg-gray-100/10"
                      style={{ color: 'var(--color-text-primary)' }}
                    >
                      <FiDownload size={14} />
                      Export as PDF
                    </button>
                    
                    <button
                      onClick={() => handleExport('json')}
                      className="flex items-center gap-2 w-full px-3 py-2 text-sm hover:bg-gray-100/10"
                      style={{ color: 'var(--color-text-primary)' }}
                    >
                      <FiDownload size={14} />
                      Export Data (JSON)
                    </button>

                    {isEditing && onDelete && (
                      <>
                        <div className="my-1" style={{ borderTop: '1px solid var(--color-border-primary)' }} />
                        <button
                          onClick={() => {
                            onDelete(widget.id);
                            setShowMenu(false);
                          }}
                          className="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-500 hover:bg-red-500/10"
                        >
                          <FiX size={14} />
                          Delete Widget
                        </button>
                      </>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Widget Content */}
      <div className="flex-1 overflow-hidden">
        {widget.isResizable !== false && isEditing ? (
          <Resizable
            size={{
              width: isMaximized ? '100%' : widget.size.width,
              height: isMaximized ? '100%' : widget.size.height - 64, // Subtract header height
            }}
            minWidth={widget.minSize?.width || 200}
            minHeight={widget.minSize?.height || 150}
            maxWidth={widget.maxSize?.width}
            maxHeight={widget.maxSize?.height}
            onResizeStop={(e: any, direction: any, ref: any, d: { width: number; height: number }) => {
              const newWidth = widget.size.width + d.width;
              const newHeight = widget.size.height + d.height;
              updateSize(newWidth, newHeight);
            }}
            enable={{
              top: false,
              right: true,
              bottom: true,
              left: false,
              topRight: false,
              bottomRight: true,
              bottomLeft: false,
              topLeft: false,
            }}
            className="h-full"
          >
            <div className="h-full p-4 overflow-auto">
              {children}
            </div>
          </Resizable>
        ) : (
          <div className="h-full p-4 overflow-auto">
            {children}
          </div>
        )}
      </div>

      {/* Loading overlay */}
      {isRefreshing && (
        <div
          className="absolute inset-0 flex items-center justify-center"
          style={{
            background: themeMode === 'dark' ? 'rgba(0,0,0,0.4)' : 'rgba(255,255,255,0.8)'
          }}
        >
          <div className="flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
            <FiRefreshCw className="animate-spin" size={16} />
            <span className="text-sm">Refreshing...</span>
          </div>
        </div>
      )}
    </motion.div>
  );

  // Click outside handler for menu
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showMenu && widgetRef.current && !widgetRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMenu]);

  return widgetContent;
};

export default CustomWidget;